import React, { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import moment from 'moment'
import { DataLoading } from '@pga/pga-component-library'
import { Button } from '../../Button'
import { getAnnouncementQuery } from '../../queries/announcement'
import { ErrorTitle } from '../Announcements/ErrorLoading'
import EllipsisModal from '../../utils/EllipsisModal'
import NoAnnouncements from '../Announcements/NoAnnouncements'
import { ARTICLE_PATHS } from './utils/constants/articleUrls'
import { resolveUrlForArticles } from './utils/urlUtils'
import {
  Container,
  Header,
  HeaderLabel,
  Divider,
  AnnouncementsList,
  AnnouncementCard,
  AnnouncementTitle,
  AnnouncementBody,
  AnnouncementLink,
  ReadMoreToggle,
  LoadingCard,
  ErrorContainer,
  NewBadge
} from './Styles/Announcements.styles'

const ANNOUNCEMENT_COUNT = 3
const SHOW_NEW_DASHBOARD_MESSAGE = true

const NewDashboardMessage = () => (
  <AnnouncementCard>
    <AnnouncementTitle>
      <NewBadge>New</NewBadge>
      Notice anything new?
    </AnnouncementTitle>
    <AnnouncementBody>
      Welcome to the new Path to Membership dashboard! Dive in and explore. If you need help or can't find what you're looking for, just{' '}
      <AnnouncementLink href={resolveUrlForArticles(ARTICLE_PATHS.GETTING_HELP)} target="_blank" rel="noopener noreferrer">
        click here
      </AnnouncementLink>
      —we're always happy to assist!
    </AnnouncementBody>
  </AnnouncementCard>
)

const AnnouncementItem = ({ announcement }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const isTruncated = announcement.body && announcement.body.length > 150

  return (
    <>
      <AnnouncementCard withAction>
        <AnnouncementTitle>
          {announcement.title || 'Announcement'}
        </AnnouncementTitle>
        <AnnouncementBody>
          {isTruncated ? `${announcement.body.substring(0, 150)}` : (announcement.body || announcement.message || '')}
          {isTruncated && (
            <>
              ... <ReadMoreToggle onClick={() => setModalOpen(true)}>Read More</ReadMoreToggle>
            </>
          )}
        </AnnouncementBody>
        {announcement.link && (
          <AnnouncementLink href={announcement.link} target="_blank" rel="noopener noreferrer">
            {announcement.label || 'Learn More'}
          </AnnouncementLink>
        )}
      </AnnouncementCard>
      {isTruncated && (
        <EllipsisModal
          open={modalOpen}
          onToggleModal={setModalOpen}
          title={announcement.title}
          message={announcement.body || announcement.message}
        />
      )}
    </>
  )
}

const LoadingPlaceholder = () => (
  <LoadingCard>
    <DataLoading h={40} />
    <DataLoading h={40} />
    <DataLoading h={40} />
    <DataLoading h={40} />
    <DataLoading h={40} />
  </LoadingCard>
)

const ErrorState = ({ refetch }) => (
  <ErrorContainer>
    <ErrorTitle>Failed to load</ErrorTitle>
    <Button onClick={() => refetch()}>REFRESH</Button>
  </ErrorContainer>
)

const Announcements = ({ 
  mainProgramTypeCode, 
  sectionName,
  authReady 
}) => {
  const { data, loading, error, refetch } = useQuery(getAnnouncementQuery, {
    variables: {
      group: mainProgramTypeCode,
      section: sectionName,
      limit: ANNOUNCEMENT_COUNT
    },
    skip: !authReady || !mainProgramTypeCode,
    fetchPolicy: 'cache-and-network'
  })

  const announcements = useMemo(() => 
    data?.announcements?.map((announcement, index) => {
      const isValidDate = announcement.date && moment(announcement.date).isValid()
      
      return {
        id: String(index + 1),
        title: announcement.title || 'Announcement',
        body: announcement.message || '',
        message: announcement.message || '',
        link: announcement.link,
        label: announcement.label,
        date: isValidDate ? announcement.date : null
      }
    }) || [],
    [data]
  )

  if (loading) {
    return <LoadingPlaceholder />
  }

  if (error) {
    return (
      <LoadingCard>
        <ErrorState refetch={refetch} />
      </LoadingCard>
    )
  }

  const hasAnnouncements = SHOW_NEW_DASHBOARD_MESSAGE || announcements.length > 0

  return (
    <Container>
      <Header>
        <HeaderLabel>Announcements</HeaderLabel>
        <Divider />
        <HeaderLabel>1 New</HeaderLabel>
      </Header>

      <AnnouncementsList>
        {hasAnnouncements ? (
          <>
            {SHOW_NEW_DASHBOARD_MESSAGE && <NewDashboardMessage />}

            {announcements.slice(0, ANNOUNCEMENT_COUNT).map((announcement, index) => {
              if (!announcement) return null
              return <AnnouncementItem key={announcement.id || index} announcement={announcement} />
            })}
          </>
        ) : (
          <NoAnnouncements />
        )}
      </AnnouncementsList>
    </Container>
  )
}

export default Announcements
